import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Row, Table } from "reactstrap";

import { adminPaths } from "admin/paths";
import { confirmAdminAction, sortByOption } from "admin/contentUtils";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import { sitePagesRepo } from "data/repositories/sitePagesRepo";

const sortOptions = [
  { value: "sort-asc", label: "Sıra" },
  { value: "title-asc", label: "Başlığa göre A-Z" },
  { value: "updated-desc", label: "Son güncellenen" },
];

export default function SitePagesList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("active");
  const [sortBy, setSortBy] = useState("sort-asc");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    try {
      setItems(sitePagesRepo.list({ includeDeleted: true }));
    } catch (loadError) {
      setError(loadError?.message || "Site sayfaları yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!location.state?.status) return;
    setFlash(location.state.status);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      if (stateFilter === "active" && item.deletedAt) return false;
      if (stateFilter === "archived" && !item.deletedAt) return false;
      if (!query) return true;
      return [item.slug, item.path, item.title, item.subtitle].join(" ").toLowerCase().includes(query);
    });

    return sortByOption(filtered, sortBy, {
      "sort-asc": (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.title.localeCompare(b.title, "tr"),
    });
  }, [items, search, sortBy, stateFilter]);

  const onArchive = (item) => {
    if (!confirmAdminAction(item.title || item.slug)) return;
    try {
      sitePagesRepo.remove(item.id);
      setFlash({ color: "success", message: "Sayfa arşivlendi." });
      load();
    } catch (archiveError) {
      setFlash({ color: "danger", message: archiveError?.message || "Arşivlenemedi" });
    }
  };

  const onSeed = () => {
    try {
      const created = sitePagesRepo.seedMissingDefaults();
      setFlash({
        color: "success",
        message: created ? `${created} varsayılan sayfa eklendi.` : "Tüm varsayılan sayfalar zaten mevcut.",
      });
      load();
    } catch (seedError) {
      setFlash({ color: "danger", message: seedError?.message || "Varsayılanlar yüklenemedi" });
    }
  };

  return (
    <>
      <AdminPageHeader
        gradient="info"
        title="Site sayfaları"
        description="Portal marketing şablonları (/about, /faq, /news…). Firestore: sitePages."
        primaryTo={{ tag: Link, to: `/admin${adminPaths.content.sitePages.create}` }}
        primaryLabel="Yeni sayfa"
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                  <h3 className="mb-0">Liste</h3>
                  <Button color="secondary" size="sm" outline onClick={onSeed}>
                    Varsayılanları yükle
                  </Button>
                </div>
                {flash ? <div className={`alert alert-${flash.color || "success"}`}>{flash.message}</div> : null}
                <AdminListControls
                  search={search}
                  onSearch={setSearch}
                  searchPlaceholder="Başlık, path veya slug ara…"
                  filters={[
                    {
                      key: "state",
                      label: "Kayıt durumu",
                      value: stateFilter,
                      onChange: setStateFilter,
                      options: [
                        { value: "active", label: "Aktif" },
                        { value: "archived", label: "Arşiv" },
                        { value: "all", label: "Tümü" },
                      ],
                    },
                  ]}
                  sortValue={sortBy}
                  onSortChange={setSortBy}
                  sortOptions={sortOptions}
                  onReset={() => {
                    setSearch("");
                    setStateFilter("active");
                    setSortBy("sort-asc");
                  }}
                  resultCount={visibleItems.length}
                  totalCount={items.length}
                />
              </CardHeader>
              <CardBody className="pt-0">
                <AdminStateBlock
                  loading={loading}
                  error={error}
                  isEmpty={!loading && !visibleItems.length}
                  emptyTitle="Site sayfası yok"
                  emptyDescription="Varsayılanları yükle veya yeni sayfa ekleyin."
                >
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col">Başlık</th>
                        <th scope="col">Yol</th>
                        <th scope="col">Sıra</th>
                        <th scope="col">Durum</th>
                        <th scope="col" />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.title}</strong>
                            <div className="text-muted text-sm">{item.subtitle}</div>
                          </td>
                          <td>
                            <code>{item.path || `/${item.slug}`}</code>
                          </td>
                          <td>{item.sortOrder || 0}</td>
                          <td>
                            {item.deletedAt ? (
                              <Badge color="secondary">Arşiv</Badge>
                            ) : item.isPublished === false ? (
                              <Badge color="warning">Taslak</Badge>
                            ) : (
                              <Badge color="success">Yayında</Badge>
                            )}
                          </td>
                          <td className="text-right">
                            <Button
                              color="primary"
                              size="sm"
                              outline
                              tag={Link}
                              to={`/admin${adminPaths.content.sitePages.edit(item.id)}`}
                            >
                              Düzenle
                            </Button>{" "}
                            {!item.deletedAt ? (
                              <Button color="danger" size="sm" outline onClick={() => onArchive(item)}>
                                Arşivle
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </AdminStateBlock>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
